'use client';

import { cn } from '@/lib/utils';

interface PasswordStrengthBarProps {
  password: string;
}

type StrengthLevel = 'weak' | 'fair' | 'strong';

function getStrength(password: string): StrengthLevel {
  const hasLetter = /[a-zA-Z]/.test(password);
  const hasNumber = /\d/.test(password);

  if (password.length < 8 || !hasLetter || !hasNumber) {
    return 'weak';
  }
  if (password.length >= 12 && hasLetter && hasNumber) {
    return 'strong';
  }
  return 'fair';
}

const STRENGTH_CONFIG: Record<
  StrengthLevel,
  { label: string; segments: number; segmentClass: string; labelClass: string }
> = {
  weak: {
    label: 'Weak',
    segments: 1,
    segmentClass: 'bg-destructive',
    labelClass: 'text-destructive',
  },
  fair: {
    label: 'Fair',
    segments: 2,
    segmentClass: 'bg-amber-500',
    labelClass: 'text-amber-500',
  },
  strong: {
    label: 'Strong',
    segments: 3,
    segmentClass: 'bg-teal-600',
    labelClass: 'text-teal-600',
  },
};

export function PasswordStrengthBar({ password }: PasswordStrengthBarProps) {
  if (!password) return null;

  const strength = getStrength(password);
  const config = STRENGTH_CONFIG[strength];

  return (
    <div className="mt-2 space-y-1.5">
      {/* Segmented bar */}
      <div className="flex gap-1">
        {[0, 1, 2].map((index) => (
          <div
            key={index}
            className={cn(
              'h-1.5 flex-1 rounded-full transition-all duration-300',
              index < config.segments
                ? config.segmentClass
                : 'bg-muted',
            )}
          />
        ))}
      </div>
      {/* Label */}
      <p
        className={cn(
          'text-[12px] font-sans font-medium transition-colors duration-300',
          config.labelClass,
        )}
      >
        {config.label}
      </p>
    </div>
  );
}
